// Shared "flag contiguous stretches in a series" algorithm for the Active groups detail-modal
// charts. WaterDetailModal (unstable water readings, negative pig tally) and MortalityDetailModal
// (negative pig tally) each walked a date-ordered series and opened/closed a Highcharts plotBand
// wherever some boolean condition held. Same walk, different predicate — extracted here so it's
// written and tested once.
//
// Pure/no Vue or store dependencies, like indexColour.ts, so it can be unit-tested in isolation.

export type FlagBand = {
    from: number;
    to: number;
    color: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_FLAG_COLOR = 'rgba(245, 158, 11, 0.12)';

/**
 * Walks a date-ordered series and returns one Highcharts plotBand per contiguous stretch where
 * `isFlagged` holds. A stretch still open at the end of the series extends one day past the last
 * point's date, so the band visibly reaches the chart's right edge instead of stopping short at
 * the last plotted point.
 */
export function computeFlagBands<T>(series: T[], dateOf: (point: T) => number, isFlagged: (point: T) => boolean, color: string = DEFAULT_FLAG_COLOR): FlagBand[] {
    const bands: FlagBand[] = [];
    let from: number | null = null;

    for (const point of series) {
        if (isFlagged(point) && from === null) from = dateOf(point);
        if (!isFlagged(point) && from !== null) {
            bands.push({ from, to: dateOf(point), color });
            from = null;
        }
    }

    if (from !== null) {
        bands.push({ from, to: dateOf(series[series.length - 1]) + DAY_MS, color });
    }

    return bands;
}
